import { reduce } from 'lodash/fp';
import { Interval } from 'luxon';
import { check } from '@mews-framework/utils';
import { DateTimePart, durationUnitByDateTimePart, unitByDateTimePart } from '../enums';
import { Factory, ToLuxon } from './types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type EndsWithS<T extends string> = T extends `${infer _}s` ? true : false;
type Plural<T extends string> = EndsWithS<T> extends true ? T : `${T}s`;

type WithAdd<T, P> = {
    [Property in P as `add${Capitalize<Plural<string & Property>>}`]: (entity: T, amount: number) => T;
};

type WithSubtract<T, P> = {
    [Property in P as `subtract${Capitalize<Plural<string & Property>>}`]: (entity: T, amount: number) => T;
};

type WithDiff<T, P> = {
    [Property in P as `diff${Capitalize<Plural<string & Property>>}`]: (entity1: T, entity2: T) => number;
};

type Manipulable<T, P> = WithAdd<T, P> & WithSubtract<T, P> & WithDiff<T, P>;

const manipulable = <T, P extends DateTimePart>(factory: Factory<T>, toLuxon: ToLuxon<T>, parts: readonly P[] = []) => {
    const toPlural = (name: string) => (name.endsWith('s') ? name : `${name}s`);

    const add = (partName: DateTimePart) => (entity: T, amount: number) => {
        check.number(amount);
        const durationUnit = durationUnitByDateTimePart[partName];

        const l = toLuxon(entity).plus({ [durationUnit]: amount });
        return factory(l);
    };

    const startOf = (partName: DateTimePart) => (entity: T) => {
        const timeUnit = unitByDateTimePart[partName];
        const l = toLuxon(entity).startOf(timeUnit);
        return factory(l);
    };

    const endOf = (partName: DateTimePart) => (entity: T) => {
        const timeUnit = unitByDateTimePart[partName];
        const l = toLuxon(entity).endOf(timeUnit);
        return factory(l);
    };

    const subtract = (partName: DateTimePart) => (entity: T, amount: number) => {
        check.number(amount);
        const durationUnit = durationUnitByDateTimePart[partName];

        const l = toLuxon(entity).minus({ [durationUnit]: amount });
        return factory(l);
    };

    const diff =
        (partName: DateTimePart, { roundDown = true } = {}) =>
        (entity1: T, entity2: T) => {
            check.sameTimezones(entity1, entity2);
            const durationUnit = durationUnitByDateTimePart[partName];
            const luxonEntity1 = toLuxon(entity1);
            const luxonEntity2 = toLuxon(entity2);

            const difference =
                luxonEntity1 < luxonEntity2
                    ? Interval.fromDateTimes(luxonEntity1, luxonEntity2).length(durationUnit)
                    : Interval.fromDateTimes(luxonEntity2, luxonEntity1).length(durationUnit);

            if (roundDown) {
                return luxonEntity1 < luxonEntity2 ? -Math.floor(difference) : Math.floor(difference);
            }
            return luxonEntity1 < luxonEntity2 ? -difference : difference;
        };

    return {
        ...reduce<P, Manipulable<T, P>>(
            (operations, partName) => ({
                ...operations,
                [`add${toPlural(partName)}`]: add(partName),
                [`subtract${toPlural(partName)}`]: subtract(partName),
                [`diff${toPlural(partName)}`]: diff(partName),
            }),
            {} as Manipulable<T, P>,
            parts
        ),
        startOf,
        endOf,
        diff,
        add,
    };
};

export default manipulable;
